import PauseCircleFilledIcon from "@mui/icons-material/PauseCircleFilled";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import { CircularProgress } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import WaveSurfer from "wavesurfer.js";
import AudioRecorder from "../../../../assets/icons/AudioRecorder";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import { getSegmentColorPalette } from "../../../../redux/selectors/segmentBuilderSelectors";
import { colors } from "../../../../utils";
import TextView from "../../../TextView";

const LoaderDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  border: 2px solid ${(props) => props.borderColor};
  border-radius: 8px;
`;

const Container = styled.div`
  padding: 12px;
  border-radius: 10px;
  background: ${(props) => props.backgroundColor};
  align-items: center;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr;
  gap: 10px;
  align-items: center;
`;

const Image = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 10px;
  object-fit: cover;
`;

const WaveContainer = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr 40px;
  background: white;
  padding: 10px 0;
  height: 60px;
  width: 100%;
  border-radius: 10px;
  align-items: center;
  justify-content: center;
`;

const Icon = styled.div`
  color: ${(props) => props.color};
  font-size: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Waveform = styled.div`
  cursor: pointer;
`;

const CurrentTime = styled(TextView)`
  margin-left: 5px;
  color: ${colors.lightGrey};
`;

const DynamicRenderer = ({ element }) => {
  const renderedElement = React.createElement(element.type, {
    ...element.props,
    key: element.key,
  });

  return <>{renderedElement}</>;
};

const AudioBlock = ({
  audio,
  textColor = colors.black,
  waveColor = colors.darkGrey,
  backgroundColor = colors.background,
  iconColor = colors.PrimaryPurple,
  showIcon = true,
}) => {
  const waveformRef = useRef(null);
  const waveSurferRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState("0:00");

  const selectedPalette = useSelector(getSegmentColorPalette);

  const { getPaletteColor } = usePaletteColor();

  useEffect(() => {
    if (audio.src && !audio.embed) {
      waveSurferRef.current = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: colors.lightGrey,
        progressColor: getPaletteColor(waveColor),
        responsive: true,
        normalize: true,
        barWidth: 2,
        width: "100%",
        height: 30,
      });

      waveSurferRef.current.load(audio.src);
      waveSurferRef.current.on("audioprocess", () => {
        const current = waveSurferRef.current.getCurrentTime();
        setCurrentTime(formatTime(current));
      });

      waveSurferRef.current.on("seeking", () => {
        const current = waveSurferRef.current.getCurrentTime();
        setCurrentTime(formatTime(current));
      });
    }

    return () => {
      if (waveSurferRef.current) waveSurferRef.current.destroy();
    };
  }, [audio, waveColor, selectedPalette]);

  const handlePlayPause = () => {
    if (waveSurferRef.current) {
      if (isPlaying) {
        waveSurferRef.current.pause();
      } else {
        waveSurferRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  if (audio.loading)
    return (
      <LoaderDiv borderColor={getPaletteColor(backgroundColor)}>
        <CircularProgress style={{ color: getPaletteColor(iconColor) }} />
      </LoaderDiv>
    );

  if (audio.embed) {
    const { embedCode } = audio;
    return (
      <div style={{ borderRadius: 8, overflow: "hidden" }}>
        <DynamicRenderer element={embedCode[0]} />
      </div>
    );
  } else
    return (
      <Container backgroundColor={getPaletteColor(backgroundColor)}>
        <TextView
          color={getPaletteColor(textColor)}
          variant="subtitle"
          style={{ WebkitLineClamp: 2, marginBottom: 10 }}
        >
          {audio.title}
        </TextView>
        <GridContainer
          style={{ gridTemplateColumns: showIcon ? "60px 1fr" : "1fr" }}
        >
          {showIcon &&
            (audio.image ? (
              <Image src={audio.image} alt={audio.title} />
            ) : (
              <AudioRecorder
                fill={getPaletteColor(iconColor)}
                width={60}
                height={60}
              />
            ))}

          <WaveContainer>
            <Icon color={getPaletteColor(iconColor)} onClick={handlePlayPause}>
              {isPlaying ? <PauseCircleFilledIcon /> : <PlayCircleFilledIcon />}
            </Icon>
            <Waveform ref={waveformRef} />
            <CurrentTime variant="tag">{currentTime}</CurrentTime>
          </WaveContainer>
        </GridContainer>
      </Container>
    );
};

export default AudioBlock;
